import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAuthDialog } from '../../contexts/AuthDialogContext';
import { logger } from '../../utils/logger';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  onClick?: () => void;
}

interface MenuSidebarProps {
  title: string;
  currentPage: string;
  menuItems: MenuItem[];
}

export default function MenuSidebar({ title, currentPage, menuItems }: MenuSidebarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();
  const { showAuthDialog } = useAuthDialog();

  const handleLogout = async () => {
    try {
      await logout();
      showAuthDialog('login');
    } catch (error) {
      logger.error('Logout failed:', error);
    }
  };

  const handleItemClick = (item: MenuItem) => {
    if (item.onClick) {
      item.onClick();
    } else {
      navigate(item.path);
    }
  };

  const isOnline = useOnlineStatus();

  // Show all menu items regardless of authentication status
  const filteredMenuItems = menuItems.filter((item) => isOnline || item.id !== 'products');

  return (
    <div className="w-full lg:w-64 flex-shrink-0">
      <div className="bg-white/90 backdrop-blur-lg rounded-2xl p-4 shadow-lg border border-white/20">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">{title}</h2>

        <div className="space-y-1 lg:space-y-2 grid grid-cols-2 gap-1 lg:grid-cols-1 lg:gap-0">
          {filteredMenuItems.map((item) => {
            const isSelected = currentPage === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item)}
                className={`group w-full flex items-center gap-1 lg:gap-3 py-1 lg:py-1.5 px-1 lg:px-2 rounded-lg lg:rounded-xl transition-all duration-300 border flex-col lg:flex-row flex-row-reverse lg:flex-row ${
                  isSelected
                    ? 'bg-blue-50/80 border-blue-200/80 text-blue-700'
                    : 'hover:bg-gray-50/80 border-transparent hover:border-gray-200/80 text-gray-700'
                }`}
              >
                <div className="relative w-8 h-8 lg:w-8 lg:h-8 flex items-center justify-center transition-all duration-300">
                  <div
                    className={`w-6 h-6 lg:w-5 lg:h-5 transition-transform duration-300 group-hover:scale-105 ${
                      isSelected ? 'text-blue-700' : 'text-gray-600'
                    }`}
                  >
                    {item.icon}
                  </div>
                </div>
                <span className="text-sm lg:text-sm font-medium flex-1 pl-1 text-left transition-colors duration-300 leading-tight lg:leading-normal">
                  {item.label}
                </span>
              </button>
            );
          })}

          {/* Logout button */}
          {isAuthenticated && isOnline && (
            <button
              onClick={handleLogout}
              className="group w-full flex items-center gap-1 lg:gap-3 py-1 lg:py-1.5 px-1 lg:px-2 rounded-lg lg:rounded-xl transition-all duration-300 border border-transparent hover:border-gray-200/80 hover:bg-gray-50/80 text-gray-700 flex-col lg:flex-row flex-row-reverse lg:flex-row"
            >
              <div className="relative w-8 h-8 lg:w-8 lg:h-8 flex items-center justify-center transition-all duration-300">
                <div className="w-6 h-6 lg:w-5 lg:h-5 transition-transform duration-300 group-hover:scale-105 text-gray-600">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                </div>
              </div>
              <span className="text-sm lg:text-sm font-medium flex-1 pl-1 text-left transition-colors duration-300 leading-tight lg:leading-normal">
                {t('logout')}
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
